import { PaginationWithProgressProps } from './props';
import PaginationWithProgressSkeleton from './Skeleton';

import styles from './styles.module.scss';
import { Button } from '../Button';
import { useTranslation } from '../../hooks';

/**
 * PaginationWithProgress Component
 * A component that displays pagination with a progress bar showing how much content has been loaded.
 * Includes a "Load More" button and handles loading states.
 *
 * @component
 * @param {Object} props - Component props
 * @param {number} props.count - Current number of items loaded
 * @param {() => void} props.fetchNextPage - Function to fetch next page of items
 * @param {boolean} props.hasNextPage - Whether there are more items to load
 * @param {boolean} props.loading - Whether the initial data is loading
 * @param {boolean} props.isFetchingNextPage - Whether the next page is being fetched
 * @param {number} props.totalCount - Total number of items available
 * @returns {JSX.Element} The rendered pagination with progress component
 *
 * @example
 * <PaginationWithProgress
 *   count={20}
 *   fetchNextPage={() => loadMore()}
 *   hasNextPage={true}
 *   loading={false}
 *   isFetchingNextPage={false}
 *   totalCount={100}
 * />
 */
export function PaginationWithProgress({
  count,
  fetchNextPage,
  hasNextPage,
  loading,
  isFetchingNextPage,
  totalCount,
}: PaginationWithProgressProps) {
  const { t } = useTranslation('common');

  /**
   * Handles clicking the "Load More" button
   * Calls fetchNextPage if it is defined
   */
  function handleFetchNextPage() {
    if (fetchNextPage != undefined) {
      fetchNextPage();
    }
  }

  if (loading) {
    return <PaginationWithProgressSkeleton />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.container__wrapper}>
        <div className={styles.container__wrapper__progress__container}>
          <div
            className={styles.container__wrapper__progress}
            style={{ width: `${(count / totalCount) * 100}%` }}
          />
        </div>
        <Button
          isSecondary
          disabled={isFetchingNextPage || !hasNextPage}
          onClick={handleFetchNextPage}
        >
          {t(
            !hasNextPage
              ? 'no_more_items_label'
              : 'pagination_load_more_button',
          )}
        </Button>
      </div>
    </div>
  );
}
